import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';
import FAQ from '../../components/ui/FAQ';
import { backendDevelopmentFAQs } from '../../data/faqs';
import { sendRequirementsFormEmail } from '../../utils/emailService';
import { scrollToElement } from '../../utils/scrollUtils';
import { useContactInfo } from '../../utils/contactObfuscation';

const BackendDevelopmentLanding = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'Backend Development',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const contactInfo = useContactInfo();

  const seoData = {
    title: 'Backend Development Services - API & Microservices | OctalChip',
    description: 'Build robust backend systems with microservices architecture, RESTful APIs, and database optimization. Get scalable server-side solutions that power your applications.',
    keywords: 'backend development, API development, microservices, database design, server-side development, RESTful APIs, GraphQL',
    canonical: 'https://www.octalchip.com/landing/backend-development',
    faqData: backendDevelopmentFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Backend Development Services",
      "description": "Robust backend systems with microservices architecture, APIs, and database optimization",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com"
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you within 24 hours with a detailed quote.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'Backend Development',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'Server',
      title: 'Scalable Architecture',
      description: 'Microservices and cloud-native designs that scale with your business'
    },
    {
      icon: 'Zap',
      title: 'High Performance',
      description: 'Optimized APIs and databases for lightning-fast response times'
    },
    {
      icon: 'Shield',
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with authentication and data protection'
    },
    {
      icon: 'Database',
      title: 'Database Optimization',
      description: 'Efficient data storage and retrieval with optimized queries'
    }
  ];

  const technologies = [
    { name: 'Node.js', description: 'JavaScript runtime for server-side' },
    { name: 'Python', description: 'Versatile backend language' },
    { name: 'PostgreSQL', description: 'Advanced relational database' },
    { name: 'MongoDB', description: 'NoSQL database for flexibility' },
    { name: 'Redis', description: 'In-memory caching solution' },
    { name: 'GraphQL', description: 'Modern API query language' }
  ];

  const useCases = [
    {
      title: 'RESTful APIs',
      description: 'Build robust REST APIs with comprehensive documentation and versioning',
      icon: 'Code'
    },
    {
      title: 'Microservices',
      description: 'Scalable microservices architecture with service mesh and API gateway',
      icon: 'Layers'
    },
    {
      title: 'Database Design',
      description: 'Optimized database schemas with proper indexing and query optimization',
      icon: 'Database'
    },
    {
      title: 'Real-time Systems',
      description: 'WebSocket and Server-Sent Events for real-time features and updates',
      icon: 'Radio'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-indigo-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  Backend Development
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-800">
                  World's Best Backend Development Services - Build Robust Systems That Scale Globally
                </h1>
                <p className="text-lg md:text-xl mb-8 text-gray-600 leading-relaxed">
                  Trusted by businesses worldwide, we create powerful server-side solutions with microservices architecture, RESTful APIs, and optimized databases. Get backend systems that are fast, secure, and built to handle millions of requests - recognized globally for excellence.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button
                    variant="primary"
                    animation="float"
                    className="px-8 py-4 text-base md:text-lg bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                    onClick={async () => {
                      await scrollToElement('#get-quote', { 
                        smooth: true,
                        maxRetries: 15,
                        retryDelay: 150
                      }).catch(() => {});
                    }}
                  >
                    Get Free Quote
                  </Button>
                  <Button
                    variant="outline"
                    animation="rotate"
                    className="px-8 py-4 text-base md:text-lg border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                    onClick={() => navigate('/services/backend-development')}
                  >
                    View Full Service
                  </Button>
                </div>

                <div className="flex flex-wrap gap-6 text-sm justify-center lg:justify-start">
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Microservices Architecture</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">API Development</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Database Optimization</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageSquare" size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Get Your Free Quote</h3>
                  <p className="text-gray-600 mb-6">Tell us about your backend project and get a detailed estimate in 24 hours</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Name"
                      required
                    />
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Email"
                      required
                    />
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Phone"
                      required
                    />
                    <textarea 
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Describe your backend project..."
                      required
                    ></textarea>
                    
                    {submitMessage && (
                      <div className={`p-4 rounded-lg text-center ${
                        submitMessage.includes('Thank you') 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                    
                    <Button 
                      type="submit"
                      variant="primary"
                      animation="shake"
                      className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">40+</div>
              <div className="text-sm md:text-base text-gray-600">API Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">99.9%</div>
              <div className="text-sm md:text-base text-gray-600">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">50ms</div>
              <div className="text-sm md:text-base text-gray-600">Avg Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">24/7</div>
              <div className="text-sm md:text-base text-gray-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Why Choose Our Backend Development?</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver backend systems that are scalable, secure, and optimized for performance
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={benefit.icon} size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Technology Stack</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We use the latest backend technologies and frameworks
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow text-center">
                <h3 className="font-bold text-gray-800 mb-2">{tech.name}</h3>
                <p className="text-sm text-gray-600">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">What We Build</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              From RESTful APIs to microservices, we build backend solutions for every need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={useCase.icon} size={24} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{useCase.title}</h3>
                <p className="text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Common Backend Development Challenges We Solve</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the pain points businesses face. Here's how we help solve them
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Slow API Response Times</h3>
                  <p className="text-gray-600">Poor backend performance leads to slow user experiences and high bounce rates. We optimize APIs with caching, database indexing, and efficient algorithms to achieve sub-100ms response times.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Scalability Issues</h3>
                  <p className="text-gray-600">Backend systems crash under high traffic loads. We build scalable architectures with microservices, load balancing, and auto-scaling to handle millions of concurrent requests.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Database Performance Problems</h3>
                  <p className="text-gray-600">Slow queries and database bottlenecks affect entire systems. We optimize databases with proper indexing, query optimization, and caching strategies for 10x performance improvements.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Security Vulnerabilities</h3>
                  <p className="text-gray-600">Data breaches can destroy your business reputation. We implement enterprise-grade security with authentication, authorization, encryption, and regular security audits.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Poor System Integration</h3>
                  <p className="text-gray-600">Disconnected systems create data silos and inefficiencies. We build well-documented RESTful and GraphQL APIs that seamlessly integrate with all your systems and third-party services.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Legacy System Maintenance</h3>
                  <p className="text-gray-600">Maintaining outdated backend systems is costly and risky. We modernize legacy systems with microservices, cloud migration, and modern architectures reducing maintenance costs by 40-50%.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Development Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures quality, transparency, and on-time delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                01
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Requirements Analysis</h3>
              <p className="text-gray-600">We analyze your business needs and technical requirements</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                02
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Architecture Design</h3>
              <p className="text-gray-600">Design scalable and secure backend architecture</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                03
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Development</h3>
              <p className="text-gray-600">Build your backend system using agile methodology</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                04
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Testing & Deployment</h3>
              <p className="text-gray-600">Comprehensive testing and production deployment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Client Success Stories</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"OctalChip built our microservices backend architecture. The system is highly scalable, secure, and handles millions of requests daily. Outstanding work!"</p>
              <div>
                <p className="font-semibold text-gray-800">David Thompson</p>
                <p className="text-sm text-gray-500">CloudScale Inc</p>
              </div>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"The RESTful API they developed is robust and well-documented. Response times are excellent and the code quality is top-notch."</p>
              <div>
                <p className="font-semibold text-gray-800">Sarah Johnson</p>
                <p className="text-sm text-gray-500">TechFlow Solutions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Backend Development Portfolio</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped businesses worldwide build successful backend systems
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Server" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Microservices Architecture</h3>
              <p className="text-gray-600 mb-4">Built a scalable microservices backend handling 10M+ requests daily with Node.js, Docker, and Kubernetes. Achieved 99.9% uptime with auto-scaling capabilities.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Node.js</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Kubernetes</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">MongoDB</span>
              </div>
              <p className="text-sm text-gray-500">Client: CloudScale Inc | Location: USA</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Code" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">RESTful API Platform</h3>
              <p className="text-gray-600 mb-4">Developed a comprehensive REST API with Python/Django featuring authentication, rate limiting, and comprehensive documentation. Serves 50+ client applications globally.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Python</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Django</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">PostgreSQL</span>
              </div>
              <p className="text-sm text-gray-500">Client: TechFlow Solutions | Location: UK</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Database" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Database Optimization</h3>
              <p className="text-gray-600 mb-4">Optimized legacy database system reducing query times by 90% with proper indexing, query optimization, and Redis caching. Improved overall system performance by 5x.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">PostgreSQL</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Redis</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">GraphQL</span>
              </div>
              <p className="text-sm text-gray-500">Client: Digital Dynamics | Location: Canada</p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offers Section */}
      <section id="offers" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Special Offers & Free Trials</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Start your backend project with confidence - try our services risk-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">POPULAR</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Play" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Trial</h3>
              <p className="text-gray-600 mb-6">Get 2 hours of free development work to experience our quality</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>2 hours of free development</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No commitment required</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep the code you receive</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full project consultation</span></li>
              </ul>
              <Button variant="primary" animation="glow" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Free Trial</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">NEW</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Gift" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Credits</h3>
              <p className="text-gray-600 mb-6">Get $500 in development credits when you sign up</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>$500 free credits</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Valid for 30 days</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Deducted from final invoice</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Use on any project</span></li>
              </ul>
              <Button variant="primary" animation="slide" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Claim Free Credits</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">GUARANTEE</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Shield" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">100% Satisfaction</h3>
              <p className="text-gray-600 mb-6">Not satisfied? Get your money back within 7 days</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>7-day money-back guarantee</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No questions asked</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep all deliverables</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full refund guarantee</span></li>
              </ul>
              <Button variant="primary" animation="rotate" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Risk-Free</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section id="trust" className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">ISO Certified</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="Lock" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="FileText" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">NDA Protected</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-quote" className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
              Start Your Backend Project Today
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-gray-800">Ready to Build Your Backend System?</h2>
            <p className="text-lg md:text-xl mb-8 text-gray-600">
              Get a free consultation and detailed quote for your backend development project.
            </p>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-xl border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Name"
                    required
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Your Phone"
                  required
                />
                <textarea 
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Describe your backend requirements..."
                  required
                ></textarea>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <Button 
                  type="submit"
                  variant="primary"
                  animation="wiggle"
                  className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Quote & Consultation'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={backendDevelopmentFAQs}
        title="Frequently Asked Questions"
        description="Common questions about our backend development services."
      />

      <Footer />
    </div>
  );
};

export default BackendDevelopmentLanding;

